import { http } from '@/services/api/client';
import type {
  ArchiveProjectPayload,
  ConfirmProjectDepartmentsPayload,
  ConfirmProjectDepartmentsResult,
  CreateProjectPayload,
  ProjectDepartmentMigrationSuggestion,
  ProjectDetail,
  ProjectManagerCandidatesResult,
  ProjectMembersResult,
  ProjectPageResult,
  QueryProjectsParams,
  UpdateProjectPayload,
} from '@/types/project';
import type { ProjectDailyReportsResult } from '@/types/project-daily-report';

export async function fetchProjects(params: QueryProjectsParams): Promise<ProjectPageResult> {
  return http.get('/projects', { params }) as Promise<ProjectPageResult>;
}

export async function fetchProject(id: string): Promise<ProjectDetail> {
  return http.get(`/projects/${id}`) as Promise<ProjectDetail>;
}

export async function createProject(payload: CreateProjectPayload): Promise<ProjectDetail> {
  return http.post('/projects', payload) as Promise<ProjectDetail>;
}

export async function updateProject(
  projectId: string,
  payload: UpdateProjectPayload,
): Promise<ProjectDetail> {
  return http.patch(`/projects/${projectId}`, payload) as Promise<ProjectDetail>;
}

export async function fetchManagerCandidates(
  departmentIds: string[],
): Promise<ProjectManagerCandidatesResult> {
  return http.get('/projects/manager-candidates', {
    params: { departmentIds },
  }) as Promise<ProjectManagerCandidatesResult>;
}

export async function fetchProjectManagerCandidates(
  projectId: string,
  departmentIds?: string[],
): Promise<ProjectManagerCandidatesResult> {
  return http.get(`/projects/${projectId}/manager-candidates`, {
    params: departmentIds?.length ? { departmentIds } : undefined,
  }) as Promise<ProjectManagerCandidatesResult>;
}

export async function fetchDepartmentMigrationSuggestion(
  projectId: string,
): Promise<ProjectDepartmentMigrationSuggestion> {
  return http.get(
    `/projects/${projectId}/department-migration-suggestion`,
  ) as Promise<ProjectDepartmentMigrationSuggestion>;
}

export async function confirmProjectDepartments(
  projectId: string,
  payload: ConfirmProjectDepartmentsPayload,
): Promise<ConfirmProjectDepartmentsResult> {
  return http.post(`/projects/${projectId}/departments/confirm`, payload) as Promise<ConfirmProjectDepartmentsResult>;
}

export async function fetchProjectMembers(projectId: string): Promise<ProjectMembersResult> {
  return http.get(`/projects/${projectId}/members`) as Promise<ProjectMembersResult>;
}

export async function archiveProject(
  projectId: string,
  payload: ArchiveProjectPayload,
): Promise<ProjectDetail> {
  return http.post(`/projects/${projectId}/archive`, payload) as Promise<ProjectDetail>;
}

export async function unarchiveProject(projectId: string): Promise<ProjectDetail> {
  return http.post(`/projects/${projectId}/unarchive`) as Promise<ProjectDetail>;
}

export async function deleteProject(projectId: string): Promise<{ id: string }> {
  return http.delete(`/projects/${projectId}`) as Promise<{ id: string }>;
}

export async function fetchProjectDailyReports(
  projectId: string,
  params: { date: string },
): Promise<ProjectDailyReportsResult> {
  return http.get(`/projects/${projectId}/daily-reports`, { params }) as Promise<ProjectDailyReportsResult>;
}
